const GetQueryParams = require('utils/get-query-params');
const AuthConstants = require('./constants');
const Debug = require('./debug');
const AuthPersistence = require('./persistence');
const MfaCooldown = require('./mfa-cooldown');
const Policy = require('./policy');
const StateTransitions = require('./state-transitions');
const Continuation = require('./continuation');

const METHODS = AuthConstants.authFlow.methods;
const REASONS = AuthConstants.authFlow.reasons;
const STATUSES = AuthConstants.authFlow.statuses;
const SOURCES = AuthConstants.authFlow.sources;
const ROUTES = AuthConstants.authFlow.routes;
const REDIRECTS = Object.freeze({
    welcome: 'welcome',
    afterAuth: 'after-auth',
    joinSchoolProfile: 'join-school-profile'
});

const ROUTING_KINDS = Object.freeze({
    authEntry: 'auth-entry',
    login: 'login',
    schoolCheck: 'school-check',
    signup: 'signup',
    passwordComplete: 'password-complete',
    postAuth: 'post-auth'
});

const internals = {};
internals.isMfaContext = (context = {}) => context.authMethod === METHODS.mfa || !!context.mfaMethod;

internals.withInput = (inputName, scopedRules) => scopedRules.map((rule) => {

    const getRuleInput = (input = {}) => {

        const scopedInput = input[inputName] || {};

        return typeof rule.select === 'function'
            ? rule.select(scopedInput)
            : scopedInput;
    };

    return {
        ...rule,
        when: (input = {}) => Object.prototype.hasOwnProperty.call(input, inputName)
            && rule.when(getRuleInput(input)),
        to: typeof rule.to === 'function'
            ? (input = {}) => rule.to(getRuleInput(input))
            : rule.to
    };
});

// Rules are evaluated from top to bottom. The first match wins.
const rules = [
    // Initial app loader auth routing.
    ...internals.withInput(ROUTING_KINDS.authEntry, [
        {
            name: 'authenticated-app',
            select: ({ selectors, state }) => ({
                isAuthenticated: selectors.getIsAuthenticated(state),
                isVerified: selectors.getVerifiedStatus(state)
            }),
            when: ({ isAuthenticated, isVerified }) => isAuthenticated && isVerified,
            to: ROUTES.app.loader
        },
        {
            name: 'incomplete-signup',
            select: ({ selectors, state }) => ({
                signupContext: selectors.getSignupContext(state)
            }),
            when: ({ signupContext = {} }) => !!signupContext.email
                && (signupContext.isVerified === false || !!signupContext.verificationToken),
            to: ({ signupContext }) => getIncompleteSignupRoute({ signupContext })
        },
        {
            name: 'login',
            select: ({ selectors, state }) => ({
                loginContext: selectors.getLoginContext(state)
            }),
            when: () => true,
            to: ({ loginContext = {} }) => resolve(ROUTING_KINDS.login, { context: loginContext })
        }
    ]),

    // Login routing. MFA intentionally precedes SSO.
    ...internals.withInput(ROUTING_KINDS.login, [
        {
            name: 'login-mfa-phone-setup',
            when: ({ context = {} }) => !!context.mfaToken && context.mfaNeedsPhone === true,
            to: ROUTES.login.mfa
        },
        {
            name: 'login-mfa-challenge',
            when: ({ context = {} }) => !!context.mfaToken,
            to: ROUTES.login.mfa
        },
        {
            name: 'login-mfa-password',
            when: ({ context = {} }) => context.authMethod === METHODS.mfa || !!context.mfaMethod,
            to: ROUTES.login.entry
        },
        {
            name: 'login-sso',
            when: ({ context = {} }) => context.authMethod === METHODS.sso,
            to: ROUTES.login.sso
        },
        {
            name: 'login-password',
            when: () => true,
            to: ROUTES.login.entry
        }
    ]),

    // School policy routing.
    ...internals.withInput(ROUTING_KINDS.schoolCheck, [
        {
            name: 'incomplete-signup-sso',
            when: ({ authMode = {} }) => authMode.isVerified === false && authMode.authMethod === METHODS.sso,
            to: ({ redirect, target }) => getRouteWithRedirect({ path: ROUTES.signup.sso, redirect, target })
        },
        {
            name: 'signup-verification',
            when: ({ authMode = {} }) => authMode.isVerified === false
                || (authMode.authMethod === METHODS.mfa
                    && (authMode.hasPassword === false || authMode.isEmailVerified === false)),
            to: ROUTES.signup.verifyEmail
        },
        {
            name: 'missing-password-reset',
            when: ({ authMode = {} }) => authMode.authMethod === METHODS.password
                && authMode.hasPassword === false,
            to: ROUTES.login.forgotPassword
        },
        {
            name: 'login-sso',
            when: ({ authMode = {} }) => authMode.authMethod === METHODS.sso,
            to: ({ redirect, target }) => getRouteWithRedirect({ path: ROUTES.login.sso, redirect, target })
        },
        {
            name: 'login-password',
            when: () => true,
            to: null
        }
    ]),

    // Signup routing. MFA challenges wait until signup prerequisites and profile completion.
    ...internals.withInput(ROUTING_KINDS.signup, [
        {
            name: 'incomplete-signup-mfa-email-verification',
            when: ({ context = {} }) => internals.isMfaContext(context) && context.isEmailVerified === false,
            to: ROUTES.signup.verifyEmail
        },
        {
            name: 'incomplete-signup-mfa-password',
            when: ({ context = {} }) => internals.isMfaContext(context) && context.hasPassword !== true,
            to: ROUTES.signup.password
        },
        {
            name: 'incomplete-signup-mfa-profile',
            when: ({ context = {} }) => internals.isMfaContext(context)
                && context.hasCompletedProfile !== true,
            to: ROUTES.signup.profile
        },
        {
            name: 'incomplete-signup-mfa-phone-setup',
            when: ({ context = {} }) => !!context.mfaToken && context.mfaNeedsPhone === true,
            to: ROUTES.login.mfa
        },
        {
            name: 'incomplete-signup-mfa-challenge',
            when: ({ context = {} }) => !!context.mfaToken,
            to: ROUTES.login.mfa
        },
        {
            name: 'incomplete-signup-mfa-resume',
            when: ({ context = {} }) => internals.isMfaContext(context)
                && context.hasCompletedProfile === true,
            to: ROUTES.signup.authenticate
        },
        {
            name: 'incomplete-signup-sso',
            when: ({ context = {} }) => !internals.isMfaContext(context)
                && context.authMethod === METHODS.sso
                && context.hasSSOToken !== true,
            to: ROUTES.signup.sso
        },
        {
            name: 'incomplete-signup-sso-terms',
            when: ({ context = {} }) => !internals.isMfaContext(context)
                && context.authMethod === METHODS.sso
                && context.hasAgreedToTerms !== true,
            to: ROUTES.signup.ssoTerms
        },
        {
            name: 'existing-member-login',
            when: ({ hasJoinedCommunity, isAuthenticated }) => !isAuthenticated && hasJoinedCommunity,
            to: ROUTES.app.loader
        },
        {
            name: 'incomplete-signup-password',
            when: ({ context = {} }) => context.authMethod !== METHODS.sso && context.hasPassword !== true,
            to: ROUTES.signup.password
        },
        {
            name: 'incomplete-signup-profile',
            when: () => true,
            to: ROUTES.signup.profile
        }
    ]),

    // Routing immediately after setting a signup password.
    ...internals.withInput(ROUTING_KINDS.passwordComplete, [
        {
            name: 'verify-email-before-continuing',
            when: ({ context = {} }) => context.isEmailVerified === false,
            to: ROUTES.signup.verifyEmail
        },
        {
            name: 'continue-authenticated-session',
            when: ({ isAuthenticated }) => isAuthenticated,
            to: ROUTES.app.welcome
        },
        {
            name: 'continue-to-signup-profile',
            when: () => true,
            to: ROUTES.signup.profile
        }
    ]),

    // Authenticated redirect routing.
    ...internals.withInput(ROUTING_KINDS.postAuth, [
        // Future join-school-mfa and join-school-sso checks belong here.
        // School joins currently trust the active authenticated session.
        {
            name: 'join-school-profile',
            when: ({ redirect }) => redirect === REDIRECTS.joinSchoolProfile,
            to: '/app/profile/edit?confirm=true'
        },
        {
            name: 'after-auth',
            when: ({ redirect }) => redirect === REDIRECTS.afterAuth,
            to: ({ target }) => internals.normalizeRedirectTarget(target) || ROUTES.app.welcome
        },
        {
            name: 'welcome',
            when: () => true,
            to: ROUTES.app.welcome
        }
    ])
];

module.exports = {
    rules,
    ROUTING_KINDS,
    METHODS,
    REDIRECTS,
    STATUSES,
    resolveAuthFlow,
    buildAuthFlowContextUpdate,
    buildSuccessfulLoginContextReset,
    getLoginPrefillEmail,
    buildPendingMfaContext,
    buildSchoolCheckFlow,
    getAppLoaderRoute,
    getAuthRouteContext,
    getRedirectContext,
    getRequestedSchoolId,
    getRedirectDestination,
    getMfaRecoveryDestination,
    guardLoginMfaRoute,
    guardProfilePhoneMfaRoute,
    getRouteWithRedirect,
    getSchoolCheckRoute,
    ensureSchoolDetails,
    isPendingMfaStatus,
    isSupportedMfaMethod,
    isSsoAuthContext,
    assertMfaAllowedForAuthContext,
    resolve,
    resolveAppLoaderAuthDestination,
    getIncompleteSignupRoute,
    routePendingMfa
};

function buildSuccessfulLoginContextReset({ email } = {}) {

    return StateTransitions.buildSuccessfulAuthentication({ email }).loginContext;
}

function getLoginPrefillEmail({ loginContext = {} } = {}) {

    return Policy.normalizeEmail(loginContext.email)
        || Policy.normalizeEmail(loginContext.lastLoginEmail)
        || '';
}

async function ensureSchoolDetails({
    actions,
    redux,
    schoolId,
    selectors,
    verificationToken
} = {}) {

    if (!schoolId || selectors.getSchoolExtensionById(redux.getState(), schoolId)) {
        return null;
    }

    if (selectors.getIsAuthenticated(redux.getState())) {
        return redux.dispatch(actions.dataFetching.fetchAppEntities({
            entities: ['schoolDetails'],
            schoolId
        }));
    }

    if (verificationToken) {
        return redux.dispatch(actions.dataFetching.fetchSchoolByVerificationToken({
            token: verificationToken
        }));
    }

    return null;
}

function resolveAuthFlow({
    currentContext = {},
    email,
    preserveCurrentMfa = false,
    response = {},
    source = SOURCES.unknown
} = {}) {

    const SsoUtils = require('utils/sso');
    const result = internals.unwrapResponse(response);
    const normalizedEmail = Policy.normalizeEmail(email || result.email || currentContext.email);
    const pendingMfa = isPendingMfaStatus(result.status);
    const configuredMfa = internals.getConfiguredMfa({
        currentContext,
        preserveCurrentMfa,
        result
    });
    const preservesCurrentMfaLane = preserveCurrentMfa
        && currentContext.authMethod === METHODS.mfa;

    const hasMfa = pendingMfa || !!configuredMfa || preservesCurrentMfaLane;
    const configuredSso = SsoUtils.normalizeSSO(
        internals.getValue(result, currentContext, 'sso')
    );
    const rawSso = hasMfa ? null : configuredSso;
    const provider = rawSso ? SsoUtils.getSSOProvider(rawSso) : null;
    const hasPassword = internals.getValue(result, currentContext, 'hasPassword');
    const isVerified = internals.getValue(result, currentContext, 'isVerified');
    const schoolPolicy = internals.getValue(result, currentContext, 'policy');

    const loginMethods = provider === 'auth0'
        ? SsoUtils.getSSOLoginMethods(rawSso)
        : null;
    const sso = rawSso;

    const hasSsoContext = !hasMfa
        && !internals.hasOwn(result, 'sso')
        && currentContext.authMethod === METHODS.sso;

    const method = pendingMfa
        ? METHODS.mfa
        : (configuredMfa || preservesCurrentMfaLane
            ? METHODS.mfa
            : ((sso || hasSsoContext) ? METHODS.sso : METHODS.password));

    const status = pendingMfa
        ? result.status
        : (method === METHODS.sso ? STATUSES.ssoRequired : STATUSES.passwordRequired);

    return {
        source,
        status,
        method,
        reason: pendingMfa
            ? REASONS.mfaChallenge
            : (configuredMfa || preservesCurrentMfaLane
                ? REASONS.mfaOverride
                : (method === METHODS.sso ? REASONS.apiSso : REASONS.apiPassword)),
        email: normalizedEmail,
        schoolId: internals.getValue(result, currentContext, 'schoolId'),
        roleId: internals.getValue(result, currentContext, 'roleId'),
        sso,
        provider,
        loginMethods,
        mfaMethod: configuredMfa,
        hasPassword,
        mfaChallenge: pendingMfa ? internals.buildMfaChallenge(result) : null,
        isVerified,
        isEmailVerified: internals.getValue(result, currentContext, 'isEmailVerified'),
        ...(typeof schoolPolicy === 'undefined' ? {} : { policy: schoolPolicy }),
        schoolOptions: internals.getValue(result, currentContext, 'schoolOptions')
    };
}

internals.resolveSchoolCheckAuthMode = ({ email, schoolCheckResult } = {}) => {

    const authFlow = resolveAuthFlow({
        email,
        response: schoolCheckResult,
        source: SOURCES.schoolCheck
    });

    const sso = authFlow.loginMethods || authFlow.sso;
    const isSso = authFlow.method === METHODS.sso;

    const authMode = {
        sso,
        authMethod: authFlow.method,
        schoolId: authFlow.schoolId,
        mfaMethod: authFlow.mfaMethod,
        hasPassword: authFlow.hasPassword,
        isVerified: authFlow.isVerified,
        isEmailVerified: authFlow.isEmailVerified,
        ...(typeof authFlow.policy === 'undefined' ? {} : { policy: authFlow.policy }),
        schoolOptions: authFlow.schoolOptions
    };

    Debug.recordFlow('auth_flow_school_check', {
        schoolId: authFlow.schoolId,
        sso,
        provider: isSso ? authFlow.provider : null,
        hasSSO: isSso,
        hasMfa: !!authFlow.mfaMethod,
        hasPassword: authFlow.hasPassword,
        mfaOverridesSSO: !!authFlow.mfaMethod && !!schoolCheckResult?.sso,
        forcePasswordLogin: authFlow.method !== METHODS.sso && !!schoolCheckResult?.sso,
        forceSSOLoginPrompt: isSso,
        hasSchoolOptions: Array.isArray(authFlow.schoolOptions) && authFlow.schoolOptions.length > 0,
        isVerified: authFlow.isVerified,
        isEmailVerified: authFlow.isEmailVerified
    });

    return authMode;
};

function buildSchoolCheckFlow({
    email,
    forceSSOLoginPrompt,
    schoolCheckResult
} = {}) {

    const authMode = internals.resolveSchoolCheckAuthMode({
        email,
        schoolCheckResult
    });

    const shouldForcePrompt = authMode.authMethod === METHODS.sso
        && forceSSOLoginPrompt !== false;

    const flowFields = {
        email,
        schoolId: authMode.schoolId,
        sso: authMode.sso,
        mfaMethod: authMode.mfaMethod,
        hasPassword: authMode.hasPassword,
        ...(typeof authMode.policy === 'undefined' ? {} : { policy: authMode.policy }),
        mfaToken: null,
        mfaPurpose: null,
        mfaNeedsPhone: null,
        mfaPhone: null,
        mfaPhoneCountry: null,
        mfaCooldownCode: null,
        mfaCooldownUntil: null,
        maskedDestination: null,
        canChangePhone: null,
        authMethod: authMode.authMethod,
        authStatus: authMode.authMethod === METHODS.sso ? STATUSES.ssoRequired : STATUSES.passwordRequired,
        authFlowSource: SOURCES.schoolCheck
    };

    return {
        authMode,
        loginContext: {
            ...flowFields,
            forceSSOLoginPromptEmail: shouldForcePrompt ? email : null,
            skipSSOLoginPromptEmail: null
        },
        signupContext: {
            ...flowFields,
            forceSSOLoginPromptEmail: authMode.authMethod === METHODS.sso ? email : null,
            skipSSOLoginPromptEmail: null,
            schoolOptions: authMode.schoolOptions,
            isVerified: authMode.isVerified,
            isEmailVerified: authMode.isEmailVerified
        }
    };
}

function buildAuthFlowContextUpdate({ authFlow, includeChallenge = false } = {}) {

    if (!authFlow) {
        return {};
    }

    const update = {
        email: authFlow.email,
        schoolId: authFlow.schoolId,
        roleId: authFlow.roleId,
        sso: authFlow.sso,
        mfaMethod: authFlow.mfaMethod,
        hasPassword: authFlow.hasPassword,
        authMethod: authFlow.method,
        authStatus: authFlow.status,
        authFlowSource: authFlow.source,
        isVerified: authFlow.isVerified,
        isEmailVerified: authFlow.isEmailVerified,
        ...(typeof authFlow.policy === 'undefined' ? {} : { policy: authFlow.policy }),
        schoolOptions: authFlow.schoolOptions
    };

    if (!includeChallenge || !authFlow.mfaChallenge) {
        return internals.removeUndefined(update);
    }

    return internals.removeUndefined({
        ...update,
        ...authFlow.mfaChallenge
    });
}

function buildPendingMfaContext({
    currentContext = {},
    request = {},
    response = {},
    source
} = {}) {

    const authoritativeSource = source
        || internals.getAuthoritativeMfaSource(currentContext);
    const resolvedSource = authoritativeSource || SOURCES.mfaChallenge;

    const authFlow = resolveAuthFlow({
        currentContext,
        email: request.email || currentContext.email,
        preserveCurrentMfa: true,
        response: {
            ...response,
            mfaPhone: request.phone || response.mfaPhone,
            mfaPhoneCountry: request.phoneCountry || response.mfaPhoneCountry
        },
        source: resolvedSource
    });

    const pendingContext = {
        ...currentContext,
        ...buildAuthFlowContextUpdate({
            authFlow,
            includeChallenge: true
        })
    };

    return authoritativeSource
        ? {
            ...pendingContext,
            mfaPurpose: authoritativeSource
        }
        : pendingContext;
}

internals.getAuthoritativeMfaSource = (context = {}) => [
    SOURCES.signupCompletionMfa,
    SOURCES.profilePhoneUpdate
].includes(context.authFlowSource)
    ? context.authFlowSource
    : null;

function isPendingMfaStatus(status) {

    return AuthConstants.authFlow.pendingMfaStatuses.includes(status);
}

function isSupportedMfaMethod(method) {

    return Object.values(AuthConstants.authFlow.mfaMethods).includes(method);
}

function isSsoAuthContext(authContext = {}) {

    return authContext.authMethod === METHODS.sso;
}

function assertMfaAllowedForAuthContext({ authContext = {}, result = {} } = {}) {

    if (isPendingMfaStatus(result?.status) && isSsoAuthContext(authContext)) {
        throw new Error('SSO login cannot require a password MFA challenge');
    }

    return result;
}

internals.unwrapResponse = (response = {}) => {

    if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
        return response.data;
    }

    return response || {};
};

internals.hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);

internals.getValue = (result, currentContext, key) => internals.hasOwn(result, key)
    ? result[key]
    : currentContext[key];

internals.getConfiguredMfa = ({ currentContext, preserveCurrentMfa, result }) => {

    if (internals.hasOwn(result, 'mfa')) {
        return result.mfa || null;
    }

    if (internals.hasOwn(result, 'mfaMethod')) {
        return result.mfaMethod || null;
    }

    if (preserveCurrentMfa && currentContext.authMethod === METHODS.mfa) {
        return currentContext.mfaMethod || null;
    }

    return null;
};

internals.buildMfaChallenge = (result) => ({
    mfaToken: result.mfaToken,
    mfaMethod: result.mfaMethod || result.mfa || null,
    mfaPurpose: result.mfaPurpose,
    mfaNeedsPhone: result.mfaNeedsPhone,
    mfaPhone: result.mfaPhone,
    mfaPhoneCountry: result.mfaPhoneCountry,
    mfaCooldownCode: MfaCooldown.isCooldownError(result) ? result.code : null,
    mfaCooldownUntil: MfaCooldown.getCooldownUntil(result),
    maskedDestination: result.maskedDestination,
    canChangePhone: result.canChangePhone
});

internals.removeUndefined = (value) => Object.keys(value).reduce((collector, key) => {

    if (typeof value[key] !== 'undefined') {
        collector[key] = value[key];
    }

    return collector;
}, {});


// Routing

function getAuthRouteContext(location) {

    const continuation = getRedirectContext(location);

    const {
        v: verificationToken,
        email,
        s: schoolId,
        r: roleId,
        sso,
        flow
    } = GetQueryParams(location);

    return {
        verificationToken,
        email,
        schoolId: schoolId ? Number(schoolId) : undefined,
        roleId: roleId ? Number(roleId) : undefined,
        sso,
        flow,
        redirect: continuation.redirect,
        target: continuation.target
    };
}

function getSchoolCheckRoute({ authMode = {}, redirect, target, email, signupContext = {} } = {}) {

    const resumableSignupContext = internals.getResumableSignupContext({
        authMode,
        email,
        signupContext
    });

    if (resumableSignupContext) {
        const resumableRoute = getIncompleteSignupRoute({
            signupContext: resumableSignupContext
        });

        if ([ROUTES.login.mfa, ROUTES.signup.authenticate].includes(resumableRoute)) {
            return null;
        }

        return {
            path: resumableRoute,
            signupContext: resumableSignupContext
        };
    }

    const input = { authMode, redirect, target };
    const routingInput = { [ROUTING_KINDS.schoolCheck]: input };
    const match = internals.findRule(routingInput);
    const path = internals.getRuleDestination(match, routingInput);

    if (match.name === 'signup-verification') {
        return {
            path,
            resendVerificationEmail: true
        };
    }

    if (match.name === 'missing-password-reset') {
        return {
            path,
            state: { email }
        };
    }

    return path ? { path } : null;
}

internals.getResumableSignupContext = ({ authMode = {}, email, signupContext = {} } = {}) => {

    const submittedEmail = Policy.normalizeEmail(email);
    const persistedEmail = Policy.normalizeEmail(signupContext.email);
    const hasPersistedSchool = signupContext.schoolId !== null
        && typeof signupContext.schoolId !== 'undefined';
    const hasCheckedSchool = authMode.schoolId !== null
        && typeof authMode.schoolId !== 'undefined';
    const schoolMismatch = hasPersistedSchool
        && hasCheckedSchool
        && signupContext.schoolId !== authMode.schoolId;

    if (authMode.isVerified !== false
        || !signupContext.verificationToken
        || !submittedEmail
        || submittedEmail !== persistedEmail
        || schoolMismatch) {
        return null;
    }

    return {
        ...signupContext,
        ...internals.removeUndefined({
            email,
            schoolId: authMode.schoolId,
            sso: authMode.sso,
            authMethod: authMode.authMethod,
            mfaMethod: authMode.mfaMethod,
            hasPassword: authMode.hasPassword,
            isVerified: authMode.isVerified,
            isEmailVerified: authMode.isEmailVerified,
            schoolOptions: authMode.schoolOptions
        })
    };
};

function routePendingMfa({
    currentContext,
    dispatch,
    request,
    returnTo,
    response,
    source,
    updateContext
} = {}) {

    const { push: Push } = require('connected-react-router');

    if (returnTo) {
        Continuation.save({ redirect: REDIRECTS.afterAuth, target: returnTo });
    }

    const pendingContext = buildPendingMfaContext({
        currentContext,
        request,
        response,
        source
    });

    dispatch(updateContext(pendingContext));
    dispatch(Push(source === SOURCES.profilePhoneUpdate
        ? ROUTES.app.verifyPhone
        : ROUTES.login.mfa));
}

function resolve(kind, input = {}) {

    if (!Object.values(ROUTING_KINDS).includes(kind)) {
        throw new Error(`Unknown routing kind "${kind}"`);
    }

    const ruleInput = kind === ROUTING_KINDS.authEntry
        ? internals.getReduxRuleInput()
        : input;

    return internals.resolveRoute({
        [kind]: ruleInput
    });
}

function resolveAppLoaderAuthDestination() {

    const authEntry = internals.getReduxRuleInput();
    const destination = internals.resolveRoute({
        [ROUTING_KINDS.authEntry]: authEntry
    });

    if (destination === ROUTES.login.entry
        && !authEntry.selectors.getIsAuthenticated(authEntry.state)) {
        return ROUTES.home;
    }

    return destination;
}

internals.getReduxRuleInput = () => {

    // Lazy loading avoids the app-context -> actions -> routing-table initialization cycle.
    const { context } = require('app-context');

    return {
        state: context.redux.hooks.getState(),
        selectors: context.selectors.all
    };
};

function getIncompleteSignupRoute({
    signupContext = {},
    hasJoinedCommunity,
    isAuthenticated
} = {}) {

    const hasAgreedToTerms = signupContext.agreeToTerms === true
        || (signupContext.authMethod !== METHODS.sso
            && (signupContext.hasPassword === true || AuthPersistence.getBrowserTermsAgreement()));

    return resolve(ROUTING_KINDS.signup, {
        context: {
            ...signupContext,
            hasAgreedToTerms
        },
        hasJoinedCommunity,
        isAuthenticated
    });
}

internals.findRule = (input = {}) => {

    const match = rules.find((rule) => rule.when(input));

    if (!match) {
        throw new Error('Routing input did not resolve');
    }

    return match;
};

internals.getRuleDestination = (rule, input = {}) => typeof rule.to === 'function'
    ? rule.to(input)
    : rule.to;

internals.resolveRoute = (input = {}) => internals.getRuleDestination(
    internals.findRule(input),
    input
);

// Redirect URL helpers

function getAppLoaderRoute({ redirect, target } = {}) {

    return getRouteWithRedirect({
        path: ROUTES.app.loader,
        redirect,
        target
    });
}

function getRouteWithRedirect({ path, redirect, target } = {}) {
    const [pathname, search = ''] = path.split('?', 2);
    const query = new URLSearchParams(search);
    query.delete('redirect');
    query.delete('target');
    const held = Continuation.peek();
    if ((held.pendingSchoolId || held.pendingProfileSchoolId) && !target) {
        Continuation.save({ ...held, redirect: redirect || REDIRECTS.welcome });
    }
    else {
        Continuation.save({ redirect, target });
    }
    const nextSearch = query.toString();

    return `${pathname}${nextSearch ? `?${nextSearch}` : ''}`;
}

function getRedirectContext(location) {

    Continuation.captureFromLocation(location);
    return Continuation.peek();
}

function getRedirectDestination(location = {}) {

    const continuation = getRedirectContext(location);
    if (continuation.pendingSchoolId && continuation.redirect === REDIRECTS.afterAuth) {
        return `/app/manage-communities?focusCommunity=${continuation.pendingSchoolId}`;
    }

    return resolve(ROUTING_KINDS.postAuth, continuation);
}

function getRequestedSchoolId(location = typeof window === 'undefined' ? {} : window.location) {

    const query = new URLSearchParams(location?.search || '');
    let value = query.get('s');
    const continuation = Continuation.peek();
    const target = internals.normalizeRedirectTarget(query.get('target')
        || (!continuation.pendingSchoolId && !continuation.pendingProfileSchoolId && continuation.target));

    if (!value && target) {
        value = new URL(target, 'https://nearpeer.net').searchParams.get('s');
    }

    const schoolId = Number(value);
    return Number.isSafeInteger(schoolId) && schoolId > 0 ? schoolId : undefined;
}

function getMfaRecoveryDestination({ loginContext = {}, signupContext = {} } = {}) {

    const loginIsProfilePhoneUpdate = loginContext.authFlowSource === SOURCES.profilePhoneUpdate
        || loginContext.mfaPurpose === SOURCES.profilePhoneUpdate;

    if (loginIsProfilePhoneUpdate) {
        return internals.normalizeRedirectTarget(Continuation.peek().target) || '/app/profile/edit';
    }

    const hasSignupChallenge = !!signupContext.mfaToken;
    const context = hasSignupChallenge ? signupContext : loginContext;

    const isSignupChallenge = hasSignupChallenge
        || context.authFlowSource === SOURCES.signupCompletionMfa
        || context.mfaPurpose === SOURCES.signupCompletionMfa;

    return isSignupChallenge ? ROUTES.signup.profile : ROUTES.login.entry;
}

function guardLoginMfaRoute({ loginContext = {}, signupContext = {} } = {}) {

    if (loginContext.mfaToken || signupContext.mfaToken) {
        return undefined;
    }

    return {
        replace: getMfaRecoveryDestination({ loginContext, signupContext })
    };
}

function guardProfilePhoneMfaRoute({ isAuthenticated, loginContext = {} } = {}) {

    const isProfilePhoneUpdate = loginContext.authFlowSource === SOURCES.profilePhoneUpdate
        || loginContext.mfaPurpose === SOURCES.profilePhoneUpdate;

    if (isAuthenticated && loginContext.mfaToken && isProfilePhoneUpdate) {
        return undefined;
    }

    return {
        replace: isAuthenticated ? '/app/profile/edit' : ROUTES.login.entry
    };
}


internals.normalizeRedirectTarget = (target) => {

    if (typeof target !== 'string' || !target.startsWith('/app/') || target.includes('\\') || target.includes('#')) {
        return null;
    }

    let decodedTarget;

    try {
        decodedTarget = decodeURI(target);
    }
    catch (err) {
        return null;
    }

    const pathname = decodedTarget.split(/[?#]/, 1)[0];

    if (!pathname.startsWith('/app/') || pathname === ROUTES.app.loader) {
        return null;
    }

    return target;
};
